/**
 * -----------------------------------------------------------------------------
 * Popup - Create New Group
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new group in the organization.
 */
import { Formik, useFormik } from "formik";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { TModalsState, setModalsData } from "../../../api/store/commonSlice";
import {
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../../components/admin/formFields";
import { routeUrls } from "../../../navigation/routeUrls";
import { useLoggedInUserData } from "../../../utils/user";
import {
  formInitialValues,
  TFormFieldNames,
  YupValidationSchema,
} from "./validation";
import {
  useCreateOrganizationGroupMutation,
  useCreateOrgGHGroupMutation,
  useLazySingleOrganizationGroupQuery,
} from "../../../api/network/adminApiServices";
import FTModal from "../../../components/common/FTModal";
import FormActionButtons from "../../../components/admin/formActionButtons";
import { RESPONSE_CODE } from "../../../api/network/constants";

const AdminsGroupsCreateNew = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.groups.create_new",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");

  // group mutation
  const [createOrgGroupAPITrigger] = useCreateOrganizationGroupMutation();
  const [createOrgGHGroupAPITrigger] = useCreateOrgGHGroupMutation();
  const [singleOrgGroupAPITrigger] = useLazySingleOrganizationGroupQuery();

  const accountHasGroupHierarchyEnabled = useLoggedInUserData("groupHierarchy");

  const hideModal = () => {
    dispatch(
      setModalsData({
        ...modalsState,
        createGroupUnder: "",
        showCreateGroup: false,
      })
    );
  };
  const formik = useFormik({
    initialValues: formInitialValues,
    validationSchema: YupValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      (accountHasGroupHierarchyEnabled === "1"
        ? createOrgGHGroupAPITrigger({
            parent_group: modalsState?.createGroupUnder || "",
            groupid: values.name,
            description: values.description,
          })
        : createOrgGroupAPITrigger({
            accountid: `${thisUserOrganizationId}`,
            groupid: values.name,
            displayname: values.name,
            description: values.description,
          })
      )
        .unwrap()
        .then((data) => {
          if (!!data?.groupid) {
            if (accountHasGroupHierarchyEnabled === "1") {
              // refetch the group hierarchy data to prevent stale data from being displayed
              dispatch(
                setModalsData({
                  ...modalsState,
                  // close create modal
                  createGroupUnder: "",
                  showCreateGroup: false,
                  // open edit modal
                  editGroupId: data?.groupid,
                  editGroupNewMode: true,
                  showEditGroup: true,
                })
              );
            } else {
              // after creating the group, fetch the group data to prevent stale data from being displayed and also prevent redirection to listing upon opening single group page
              singleOrgGroupAPITrigger({
                accountid: `${thisUserOrganizationId}`,
                groupid: data?.groupid,
              })
                .unwrap()
                .then((dataSingleGroup) => {
                  if (!!dataSingleGroup?.results.length) {
                    navigate(
                      `${routeUrls.dashboardChildren.adminChildren.groups}/${data?.groupid}`,
                      { state: { new: true } }
                    );
                  } else {
                    // toast.error(t('toast.create_failed'));
                    // disabled toast error message as there could be a case where the user does not have access to the newly created group
                    // so, we are not showing the error message
                    // instead just closing the modal
                  }
                })
                .catch((error) => {
                  const errors = !!error?.data
                    ? serializeErrorKeyValues(error?.data)
                    : [t("create_failed")];
                  toast.error(errors?.join(" "));
                })
                .finally(() => {
                  // show success toast in either case because the group is created
                  toast.success(t("toast.create_success"), {
                    autoClose: 10000,
                  }); // 10 seconds to allow user to read the longer message
                  // close the modal
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      createGroupUnder: "",
                      showCreateGroup: false,
                    })
                  );
                });
            }
          } else {
            toast.error(t("toast.create_failed"));
          }
        })
        .catch((error) => {
          // if (error?.status === RESPONSE_CODE.BAD_REQUEST) {
          //   return;
          // } // Commented 27/8/2025 in order to show error for already existing group name
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("create_failed")];
          toast.error(errors?.join(" "));
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    dirty,
    setFieldValue,
  } = formik;

  useEffect(() => {
    if (modalsState.showCreateGroup) {
      formik.resetForm();
    }
  }, [modalsState.showCreateGroup]);

  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;
  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              label: t("submit_button"),
              variant: "primary",
              disabled: !isFormValid || isSubmitting /* || isLoading*/,
            },
            {
              label: tMain("cancel"),
              type: "button",
              variant: "danger-transparent",
              disabled: isSubmitting /* || isLoading*/,
              onClick: () => hideModal(),
            },
          ]}
        />
      </>
    );
  }, [isFormValid, isSubmitting]);
  return (
    <FTModal
      visible={modalsState.showCreateGroup}
      heading={t("heading")}
      containerStyle="max-w-[calc(100vw-4rem)] w-[560px]"
      onHide={hideModal}
    >
      {/* <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none"> */}
      <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
        <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
          {t("sub_heading")}
        </p>

        <AdminFormFieldInput
          label={t("name")}
          type="text"
          id="GroupName"
          name="name"
          placeholder={t("name_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.name}
          touched={touched.name}
          error={errors.name}
        />

        <AdminFormFieldInput
          label={t("description")}
          type="text"
          id="GroupDescription"
          name="description"
          placeholder={t("description_placeholder")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.description}
          touched={touched.description}
          error={errors.description}
        />
        {MemoizedFormActionButtons}
        {/* <AdminFormFieldSubmit
              label={t('submit_button')}
              variant="primary"
              disabled={!isFormValid || isSubmitting }// || isLoading
            />

            <AdminFormFieldSubmit
              label={tMain('cancel')}
              type="button"
              variant="danger-transparent"
              disabled={isSubmitting }// || isLoading
              onClick={hideModal}
            /> */}
      </form>
    </FTModal>
  );
};
export default AdminsGroupsCreateNew;
